import React from "react";
import ConnectingCurveAfter from "../images/Curves/CurvedAfterFeatures.svg";
import ConnectingCurve2 from "@images/Curves/Curve2Features2.svg";
import { CheckIcon } from "@heroicons/react/outline";
import { GatsbyImage } from "gatsby-plugin-image";
import { Link } from "gatsby";
import type { DeepNonNullable } from "ts-essentials";

/**
 * To drive up our Google Lighthouse SEO score, prefer descriptive link texts
 */
const getProductPageLinkText = (productPageSlug: string): string => {
  if (productPageSlug === "keyword-rank-tracker") {
    return "Keyword Rank Tracker";
  }
  return "Read more";
};

const Feature = ({
  feature,
}: {
  feature: DeepNonNullable<Queries.LandingPageDataQuery>["strapiFrontpage"]["features"][number];
}) => {
  const { title, subtitle, productPage, featurePoints, image } = feature;

  return (
    <div className="relative mt-3 flex w-full flex-grow-0 flex-col items-center gap-x-20 lg:flex-row lg:px-20 lg:pt-0 lg:pb-20">
      <div className="relative order-1 h-full w-full lg:order-2 lg:w-1/2">
        {!image.localFile.childImageSharp &&
        image.localFile.extension === "svg" ? (
          <img
            className="my-6 lg:left-12 lg:h-1/2"
            src={image.localFile.publicURL}
            alt={image.alternativeText}
            style={{
              maxWidth: "1200px",
              position: "relative",
            }}
          />
        ) : (
          <GatsbyImage
            imgClassName="h-1/2"
            className="absolute my-6 lg:left-12 lg:h-1/2"
            objectFit="contain"
            style={{
              maxWidth: "1200px",
              position: "relative",
            }}
            key={image.id}
            image={image.localFile.childImageSharp.gatsbyImageData}
            alt={image.alternativeText}
          />
        )}
      </div>
      <div className="lg:w-1/2">
        <div className="order-2 w-full lg:order-1">
          <h2 className="font-sans text-3xl font-normal text-accuRankerOrange-500">
            {title}
          </h2>
          <p className="mt-1 text-justify text-xl text-white">
            {subtitle.data.childMarkdownRemark.rawMarkdownBody}
          </p>
        </div>
        <div className="mt-6 flex flex-col space-y-5">
          {/* Bullet Element */}
          {featurePoints.map((point, ix) => (
            <div className="flex flex-row" key={ix}>
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-accuRankerPurple-500">
                <CheckIcon className="h-14 w-14 p-4 text-white" />
              </div>
              <div className="ml-5 pt-4">
                <p className="mb-0 text-white">{point.title}</p>
                {point.subtitle && (
                  <div className="text-justify text-sm text-gray-400">
                    {point.subtitle.data.childMarkdownRemark.rawMarkdownBody !==
                      "NULL" &&
                      point.subtitle.data.childMarkdownRemark.rawMarkdownBody}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
        {productPage && productPage.slug && (
          <div className="mt-6">
            <Link
              to={"/" + productPage.slug + "/"}
              className="transition-color rounded-md border-2 border-accuRankerOrange-500 py-1 px-2 font-medium text-white text-accuRankerOrange-500 duration-300 hover:bg-accuRankerOrange-500 hover:text-white"
            >
              {getProductPageLinkText(productPage.slug)}
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

const FeatureSection = (props: {
  data: DeepNonNullable<Queries.LandingPageDataQuery>["strapiFrontpage"]["features"];
}) => {
  const allFeatures = props.data;

  return (
    <div className="div-block mt-10 md:mt-0 md:pt-0">
      <div className="relative w-full">
        <img
          className="hidden lg:block"
          src={ConnectingCurve2}
          style={{
            width: "100%",
            marginBottom: "-3%",
          }}
          alt="connecting curve image"
        />
      </div>
      <div
        className="flex w-full flex-col justify-around"
        style={{
          marginTop: "2%",
        }}
      >
        <div className="relative lg:bg-[url('../images/Curves/StraigthLines.svg')] lg:bg-[length:100%_auto] lg:bg-center lg:bg-repeat-y">
          {allFeatures.map((feature, ix: number) => (
            <Feature key={ix} feature={feature} />
          ))}
        </div>
      </div>
      <div className="relative w-full" style={{ zIndex: -1 }}>
        <img
          className="hidden lg:block"
          src={ConnectingCurveAfter}
          style={{
            width: "100%",
            marginBottom: "-3%",
          }}
          alt="connecting curve image"
        />
      </div>
    </div>
  );
};

export default FeatureSection;
